import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import WorkoutNameEditor from "../WorkoutNameEditor/WorkoutNameEditor";
import axiosClient from "../../axiosClient";
import FullWidthAsyncButton from "../FullWidthButton/FullWidthAsyncButton";

interface EditWorkoutDetailsModalProps {
  isOpen: boolean;
  onModalClose: (updatedWorkoutId?: string) => void;
  workoutToEdit: Workout | null;
  previousWorkouts: string[];
}

const EditWorkoutDetailsModal: React.FC<EditWorkoutDetailsModalProps> = ({
  isOpen,
  onModalClose,
  workoutToEdit,
  previousWorkouts,
}) => {
  const [workoutName, setWorkoutName] = useState<string>("");
  const [workoutDate, setWorkoutDate] = useState<Date>(new Date());

  useEffect(() => {
    if (workoutToEdit) {
      setWorkoutName(workoutToEdit.title);
      setWorkoutDate(new Date(workoutToEdit.date));
    }
  }, [workoutToEdit]);

  const handleClose = (updatedWorkoutId?: string) => {
    onModalClose(updatedWorkoutId);
  };

  const handleWorkoutSubmitted = async () => {
    const updatedWorkout = {
      title: workoutName.trim(),
      date: workoutDate.toISOString(),
    };

    try {
      const updateWorkoutResponse = await axiosClient.put(
        `/api/workouts/${workoutToEdit?.id}`,
        updatedWorkout
      );

      if (updateWorkoutResponse.status !== 200) {
        throw new Error(updateWorkoutResponse.data.message);
      }

      handleClose(workoutToEdit?.id);
    } catch (e: any) {
      console.error("Error updating workout:", e);
    }
  };

  const currentWorkDate = new Date(workoutToEdit?.date || "").toDateString();
  const newWorkoutDate = workoutDate.toDateString();

  const containsEdits =
    workoutName.trim() !== workoutToEdit?.title ||
    currentWorkDate !== newWorkoutDate;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => handleClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white text-left shadow-xl transition-all mx-4 mb-24 w-full">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <XMarkIcon
                    className="h-5 w-auto text-gray-400"
                    aria-hidden="true"
                    onClick={() => handleClose()}
                  />
                </div>

                <div className="bg-white rounded-lg px-4 pt-5 pb-3">
                  <div className="mb-4">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
                      <PencilSquareIcon
                        className="h-6 w-6 text-indigo-600"
                        aria-hidden="true"
                      />
                    </div>
                    <Dialog.Title
                      as="h3"
                      className="mt-3 text-xl font-medium leading-6 text-gray-900 w-full text-center"
                    >
                      Edit Workout
                    </Dialog.Title>
                  </div>
                  <div className="flex flex-col gap-y-4">
                    <WorkoutNameEditor
                      workoutName={workoutName}
                      previousWorkouts={previousWorkouts}
                      onWorkoutNameChange={(workoutName: string) =>
                        setWorkoutName(workoutName)
                      }
                    />
                    <div>
                      <label
                        htmlFor="workout-date"
                        className="block mb-1 text-sm font-medium text-gray-700"
                      >
                        Workout Date
                      </label>
                      <DatePicker
                        name="workout-date"
                        tabIndex={-1}
                        autoComplete="off"
                        selected={workoutDate}
                        onChange={(date: Date) => setWorkoutDate(date)}
                        popperPlacement="bottom"
                        onFocus={(e: { target: { readOnly: boolean } }) =>
                          (e.target.readOnly = true)
                        }
                        onKeyDown={(e: { preventDefault: () => void }) => {
                          e.preventDefault();
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <FullWidthAsyncButton
                    className="mb-3"
                    text="Save"
                    loadingText="Saving changes..."
                    onClick={handleWorkoutSubmitted}
                    disabled={!containsEdits}
                  />
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => {
                      handleClose();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EditWorkoutDetailsModal;
